/** Corey / table relative-permeability curves for MB UI charts. */

export type RelPermPoint = { sw: number; krw: number; kro: number };

/** Chart row: null = hide this series value at Sw (zeros are not drawn). */
export type RelPermChartPoint = {
  sw: number;
  krw: number | null;
  kro: number | null;
};

/** Fixed Sw nodes for the editor table (0 … 1, step 0.1). */
export const SW_GRID_01: number[] = Array.from({ length: 11 }, (_, i) =>
  Number((i / 10).toFixed(1)),
);

/** Typical Corey shape presets (endpoints still come from So / E_d). */
export type RelPermPresetId =
  | 'sandstone_ww'
  | 'sandstone_clay'
  | 'carbonate_mixed'
  | 'oil_wet'
  | 'default_corey';

export type RelPermPreset = {
  id: RelPermPresetId;
  label: string;
  description: string;
  krw0: number;
  nw: number;
  no: number;
};

export const RELPERM_PRESETS: RelPermPreset[] = [
  {
    id: 'default_corey',
    label: 'Default Corey',
    description: 'Same shape as the auto curve from So / E_d (krw0=0.35, nw=2, no=3).',
    krw0: 0.35,
    nw: 2,
    no: 3,
  },
  {
    id: 'sandstone_ww',
    label: 'Clean sandstone, water-wet',
    description: 'Moderate water endpoint, classic water-wet exponents.',
    krw0: 0.4,
    nw: 2,
    no: 3,
  },
  {
    id: 'sandstone_clay',
    label: 'Clayey sandstone',
    description: 'Lower krw endpoint, steeper curves (higher exponents).',
    krw0: 0.25,
    nw: 3,
    no: 4,
  },
  {
    id: 'carbonate_mixed',
    label: 'Carbonate, mixed-wet',
    description: 'Higher water endpoint, closer oil/water exponents.',
    krw0: 0.5,
    nw: 2.5,
    no: 2.5,
  },
  {
    id: 'oil_wet',
    label: 'Strongly oil-wet',
    description: 'High krw at residual oil; flatter water, steeper oil.',
    krw0: 0.7,
    nw: 1.5,
    no: 4,
  },
];

export type RelPermPropsConflict = {
  field: 'so' | 'kvyt';
  label: string;
  current: number;
  implied: number;
};

/** One Sw-grid step — endpoints on the 0.1 table cannot be finer than this. */
export const RELPERM_ENDPOINT_TOL = 0.1;

function num(v: unknown, d: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function round3(n: number): number {
  return Number(n.toFixed(3));
}

export function coreyRelPermCurve(
  opts: {
    so?: number | '';
    kvyt?: number | '';
    krw0?: number;
    nw?: number;
    no?: number;
  },
  n = 40,
): { swi: number; sor: number; pts: RelPermPoint[] } {
  const so = num(opts.so, 0.5);
  const kvyt = num(opts.kvyt, 0.4);
  const krw0 = num(opts.krw0, 0.35);
  const nw = num(opts.nw, 2);
  const no = num(opts.no, 3);
  const swi = Math.max(0, Math.min(0.9, 1 - so));
  const sor = Math.max(0.01, so * (1 - kvyt));
  const denom = Math.max(1e-6, 1 - swi - sor);
  const pts: RelPermPoint[] = [];
  for (let i = 0; i <= n; i++) {
    const sw = swi + denom * (i / n);
    const swn = (sw - swi) / denom;
    const son = (1 - sw - sor) / denom;
    pts.push({
      sw: +sw.toFixed(5),
      krw: swn > 0 ? +(krw0 * swn ** nw).toFixed(6) : 0,
      kro: son > 0 ? +(son ** no).toFixed(6) : 0,
    });
  }
  return { swi, sor, pts };
}

/**
 * Corey → Sw grid with physical plateaus:
 * Sw ≤ Swi → krw=0, kro=1; Sw ≥ 1−Sor → krw=krw0, kro=0; else Corey.
 * Avoids the sparse “two-point stub” when the mobile band is narrower than 0.1.
 */
export function coreyToSwGrid(opts: {
  so?: number | '';
  kvyt?: number | '';
  krw0?: number;
  nw?: number;
  no?: number;
}): RelPermPoint[] {
  const krw0 = num(opts.krw0, 0.35);
  const { swi, sor, pts } = coreyRelPermCurve({ ...opts, krw0 });
  return SW_GRID_01.map((sw) => {
    if (sw <= swi + 1e-9) {
      return { sw, krw: 0, kro: 1 };
    }
    if (sw >= 1 - sor - 1e-9) {
      return { sw, krw: Number(krw0.toFixed(3)), kro: 0 };
    }
    const { krw, kro } = interpKrAtSw(pts, sw);
    return {
      sw,
      krw: Number(krw.toFixed(3)),
      kro: Number(kro.toFixed(3)),
    };
  });
}

/** Build a Sw-grid table from a shape preset + current So / E_d. */
export function relPermPtsFromPreset(
  presetId: RelPermPresetId,
  so: number,
  kvyt: number,
): RelPermPoint[] {
  const preset = RELPERM_PRESETS.find((p) => p.id === presetId) ?? RELPERM_PRESETS[0];
  return coreyToSwGrid({
    so,
    kvyt,
    krw0: preset.krw0,
    nw: preset.nw,
    no: preset.no,
  });
}

/**
 * Endpoints from phase presence: Swi = first Sw with krw>0, Sor = 1 − last Sw with kro>0.
 * Ignores kro=1 / krw=krw0 plateaus outside the mobile band.
 */
export function inferRelPermEndpoints(pts: RelPermPoint[]): { swi: number; sor: number } {
  if (!pts.length) return { swi: 0, sor: 0 };
  const sorted = [...pts].sort((a, b) => a.sw - b.sw);
  const firstKrw = sorted.find((p) => p.krw > 1e-12);
  const lastKro = [...sorted].reverse().find((p) => p.kro > 1e-12);
  const swi = firstKrw ? firstKrw.sw : sorted[0].sw;
  const swHi = lastKro ? lastKro.sw : sorted[sorted.length - 1].sw;
  return { swi, sor: Math.max(0, 1 - swHi) };
}

/** So / E_d implied by curve endpoints (same relations as Corey). */
export function impliedPropsFromEndpoints(
  swi: number,
  sor: number,
): { so: number; kvyt: number } {
  const so = Math.max(0.05, Math.min(0.95, 1 - swi));
  const kvyt =
    so > 1e-6
      ? Math.max(0.05, Math.min(0.95, 1 - sor / so))
      : 0.45;
  return { so: round3(so), kvyt: round3(kvyt) };
}

/**
 * Compare curve endpoints to Properties So / E_d.
 * Tolerance ≈ one Sw-grid step (0.1).
 */
export function findRelPermPropsConflicts(
  pts: RelPermPoint[],
  so: number,
  kvyt: number,
  tol = RELPERM_ENDPOINT_TOL,
): RelPermPropsConflict[] {
  const { swi, sor } = inferRelPermEndpoints(pts);
  const expectedSwi = Math.max(0, Math.min(0.9, 1 - so));
  const expectedSor = Math.max(0.01, so * (1 - kvyt));
  const implied = impliedPropsFromEndpoints(swi, sor);
  const conflicts: RelPermPropsConflict[] = [];
  if (Math.abs(swi - expectedSwi) > tol + 1e-9) {
    conflicts.push({
      field: 'so',
      label: 'So',
      current: round3(so),
      implied: implied.so,
    });
  }
  if (Math.abs(sor - expectedSor) > tol + 1e-9) {
    conflicts.push({
      field: 'kvyt',
      label: 'E_d',
      current: round3(kvyt),
      implied: implied.kvyt,
    });
  }
  return conflicts;
}

export function relPermPtsFromTable(table?: number[][] | null): RelPermPoint[] {
  if (!table?.length) return [];
  return table
    .map((r) => ({
      sw: Number(r[0]),
      krw: Number(r[1]),
      kro: Number(r[2]),
    }))
    .filter((p) => Number.isFinite(p.sw) && Number.isFinite(p.krw) && Number.isFinite(p.kro))
    .sort((a, b) => a.sw - b.sw);
}

/** Sw shown / stored at 0.1 precision. */
export function roundSw01(sw: number): number {
  return Math.round(sw * 10) / 10;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linear interpolate krw/kro from sorted source curve at a given Sw. */
export function interpKrAtSw(pts: RelPermPoint[], sw: number): { krw: number; kro: number } {
  if (!pts.length) return { krw: 0, kro: 0 };
  const sorted = [...pts].sort((a, b) => a.sw - b.sw);
  if (sw <= sorted[0].sw) return { krw: sorted[0].krw, kro: sorted[0].kro };
  const last = sorted[sorted.length - 1];
  if (sw >= last.sw) return { krw: last.krw, kro: last.kro };
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (sw >= a.sw && sw <= b.sw) {
      const t = (sw - a.sw) / Math.max(b.sw - a.sw, 1e-12);
      return {
        krw: lerp(a.krw, b.krw, t),
        kro: lerp(a.kro, b.kro, t),
      };
    }
  }
  return { krw: last.krw, kro: last.kro };
}

/**
 * Map any curve onto the fixed Sw grid 0…1 (step 0.1).
 * Outside the source Sw range, values are 0 (not drawn on the chart).
 */
export function resampleToSwGrid(pts: RelPermPoint[]): RelPermPoint[] {
  if (!pts.length) {
    return SW_GRID_01.map((sw) => ({ sw, krw: 0, kro: 0 }));
  }
  const sorted = [...pts].sort((a, b) => a.sw - b.sw);
  const swLo = sorted[0].sw;
  const swHi = sorted[sorted.length - 1].sw;
  return SW_GRID_01.map((sw) => {
    if (sw < swLo - 1e-9 || sw > swHi + 1e-9) {
      return { sw, krw: 0, kro: 0 };
    }
    const { krw, kro } = interpKrAtSw(sorted, sw);
    return {
      sw,
      krw: Number(krw.toFixed(3)),
      kro: Number(kro.toFixed(3)),
    };
  });
}

export function relPermPtsToTable(pts: RelPermPoint[]): number[][] {
  return resampleToSwGrid(pts).map((p) => [p.sw, p.krw, p.kro]);
}

/** Hide zero kr values so the line only draws where the phase is present. */
export function maskZeroKrForChart(pts: RelPermPoint[]): RelPermChartPoint[] {
  return pts.map((p) => ({
    sw: p.sw,
    krw: p.krw > 0 ? p.krw : null,
    kro: p.kro > 0 ? p.kro : null,
  }));
}

/** Sw range where at least one phase has kr > 0 (exclude both-zero tails). */
export function relPermActiveSwDomain(pts: RelPermPoint[]): [number, number] {
  const active = pts.filter((p) => p.krw > 1e-12 || p.kro > 1e-12);
  if (!active.length) return [0, 1];
  const sws = active.map((p) => p.sw);
  const lo = Math.min(...sws);
  const hi = Math.max(...sws);
  if (hi - lo < 1e-9) {
    return [Math.max(0, lo - 0.05), Math.min(1, hi + 0.05)];
  }
  return [lo, hi];
}

/** Dense Sw samples for a smooth chart within the active (non both-zero) band. */
export function densifyRelPermForChart(pts: RelPermPoint[], n = 50): RelPermPoint[] {
  if (pts.length < 2) return pts;
  const [swMin, swMax] = relPermActiveSwDomain(pts);
  const span = Math.max(swMax - swMin, 1e-9);
  const out: RelPermPoint[] = [];
  for (let i = 0; i <= n; i++) {
    const sw = Number((swMin + (span * i) / n).toFixed(5));
    const { krw, kro } = interpKrAtSw(pts, sw);
    out.push({
      sw,
      krw: Number(krw.toFixed(6)),
      kro: Number(kro.toFixed(6)),
    });
  }
  return out;
}

export function relPermChartMeta(pts: RelPermPoint[]) {
  const { swi, sor } = inferRelPermEndpoints(pts);
  const krs = pts.flatMap((p) => [p.krw, p.kro]).filter((v) => v > 0);
  const krMax = krs.length ? Math.max(...krs) : 1;
  const xDomain = relPermActiveSwDomain(pts);
  return {
    // Clip Sw where both krw and kro are zero (edit + display)
    xDomain,
    yDomain: [0, Math.max(krMax, 1)] as [number, number],
    swi,
    sor,
  };
}
